import { useEffect, useMemo, useState } from 'react'
import type { ParentTicketStatus } from '@/types/parentTicket'

type FormState = {
  status: ParentTicketStatus
  priority: 'low' | 'medium' | 'high' | 'urgent'
  group: string
  agent: string
  category: string
  subCategory: string
  supportType: string
  warranty: string
  billable: string
}

type TicketShape = {
  id: string
  status: ParentTicketStatus
  severity?: string
  warranty?: string
  billable?: string
  group?: string
  agent?: string
  category?: string
  subCategory?: string
  supportType?: string | null
}

const DEFAULT_FORM: FormState = {
  status: 'Open',
  priority: 'medium',
  group: 'logistics',
  agent: 'joseph',
  category: 'hardware',
  subCategory: 'laptop',
  supportType: 'onsite',
  warranty: 'yes',
  billable: 'non_billable',
}

function toPriorityValue(label?: string): FormState['priority'] {
  const v = (label || '').toLowerCase()
  if (v === 'low') return 'low'
  if (v === 'mid' || v === 'medium') return 'medium'
  if (v === 'high') return 'high'
  if (v === 'urgent') return 'urgent'
  return 'medium'
}

function toPriorityLabel(value: FormState['priority']) {
  if (value === 'low') return 'Low'
  if (value === 'medium') return 'Medium'
  if (value === 'high') return 'High'
  if (value === 'urgent') return 'Urgent'
  return 'Medium'
}

export function useParentTicketDetailForm(ticket?: TicketShape | null) {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)

  useEffect(() => {
    if (!ticket) return
    setForm((p) => ({
      ...p,
      status: ticket.status ?? p.status,
      priority: toPriorityValue(ticket.severity),
      warranty: ticket.warranty ?? p.warranty,
      billable: ticket.billable ?? p.billable,
      group: ticket.group ?? p.group,
      agent: ticket.agent ?? p.agent,
      category: ticket.category ?? p.category,
      subCategory: ticket.subCategory ?? p.subCategory,
      supportType: ticket.supportType ?? p.supportType,
    }))
  }, [ticket?.id])

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((p) => ({ ...p, [key]: value }))
  }

  const payload = useMemo(() => {
    return {
      status: form.status,
      severity: toPriorityLabel(form.priority),
      group: form.group,
      agent: form.agent,
      category: form.category,
      subCategory: form.subCategory,
      supportType: form.supportType,
      warranty: form.warranty,
      billable: form.billable,
    }
  }, [form])

  return { form, setField, payload }
}
